import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from '../LoadingSpinner';
import './FragranceGroupView.css';

function FragranceGroupView() {
  const [fragranceGroups, setFragranceGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedWorkOrders, setSelectedWorkOrders] = useState(new Set());
  const [workOrderQuantities, setWorkOrderQuantities] = useState({});
  const [totals, setTotals] = useState({ totalOil: 0, totalWax: 0, totalWeight: 0, totalUnits: 0 });
  const [creatingBatch, setCreatingBatch] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const OIL_PERCENTAGE = 0.09090909; // 9.090909% oil

  useEffect(() => {
    // Only fetch if data not already loaded
    if (!isDataLoaded) {
      fetchFragranceGroups();
    }
  }, []);

  useEffect(() => {
    calculateSelectedTotals();
  }, [selectedWorkOrders, workOrderQuantities, fragranceGroups]);

  const fetchFragranceGroups = async (forceRefresh = false) => {
    // Skip if already loaded and not forcing refresh
    if (isDataLoaded && !forceRefresh) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get('/api/workorders/fragrance-groups');

      // Process the data to calculate materials
      const processedGroups = processGroupData(response.data);
      setFragranceGroups(processedGroups);

      // Calculate grand totals
      calculateTotals(processedGroups);
      setError(null);
      setIsDataLoaded(true);
    } catch (err) {
      console.error('Failed to fetch fragrance groups:', err);
      setError('Failed to load fragrance groups');
    } finally {
      setLoading(false);
    }
  };

  const processGroupData = (groups) => {
    const processedGroups = groups.map(group => {
      let totalOilNeeded = 0;
      let totalWaxNeeded = 0;
      let totalUnits = 0;

      // Find the highest priority work order in the group
      const priorityOrder = { 'Critical': 3, 'High': 2, 'Normal': 1, 'Low': 0 };
      let highestPriority = null;
      group.workOrders.forEach(order => {
        if (!highestPriority ||
            (priorityOrder[order.priority] || 0) > (priorityOrder[highestPriority] || 0)) {
          highestPriority = order.priority;
        }
      });

      // Calculate materials for each work order in the group
      const processedOrders = group.workOrders.map(order => {
        const caseQty = order.caseQty || 1;
        const ozFill = order.ozFill || 0;
        // Use remaining quantity for calculations instead of original quantity
        const quantity = order.quantityRemaining !== undefined ? order.quantityRemaining : order.quantity || 0;

        // Total units to produce
        const totalUnitsForOrder = quantity * caseQty;

        // Total weight in oz for this order
        const totalWeight = totalUnitsForOrder * ozFill;

        // Calculate oil and wax in ounces, then convert to pounds
        const oilNeededOz = totalWeight * OIL_PERCENTAGE;
        const waxNeededOz = totalWeight * (1 - OIL_PERCENTAGE);

        // Convert to pounds for display
        const oilNeededLbs = oilNeededOz / 16;
        const waxNeededLbs = waxNeededOz / 16;

        totalOilNeeded += oilNeededOz;
        totalWaxNeeded += waxNeededOz;
        totalUnits += totalUnitsForOrder;

        return {
          ...order,
          totalUnits: totalUnitsForOrder,
          totalWeight,
          oilNeeded: oilNeededLbs.toFixed(2),
          waxNeeded: waxNeededLbs.toFixed(2)
        };
      });

      return {
        ...group,
        workOrders: processedOrders,
        highestPriority: highestPriority,
        totalOilNeeded: (totalOilNeeded / 16).toFixed(2), // Convert to lbs
        totalOilNeededRaw: totalOilNeeded / 16, // Keep raw number for sorting
        totalWaxNeeded: (totalWaxNeeded / 16).toFixed(2), // Convert to lbs
        totalWeight: ((totalOilNeeded + totalWaxNeeded) / 16).toFixed(2), // Convert to lbs
        totalUnits
      };
    });

    // Sort by total oil needed (high to low)
    return processedGroups.sort((a, b) => b.totalOilNeededRaw - a.totalOilNeededRaw);
  };

  const calculateTotals = (groups) => {
    // This now only processes group data, not sets totals
    // Actual totals are calculated based on selected work orders
  };

  const calculateSelectedTotals = () => {
    let totalOil = 0;
    let totalWax = 0;
    let totalUnits = 0;

    fragranceGroups.forEach(group => {
      group.workOrders.forEach(order => {
        if (selectedWorkOrders.has(order.id)) {
          const quantity = workOrderQuantities[order.id] || order.quantity;
          const caseQty = order.caseQty || 1;
          const ozFill = order.ozFill || 0;
          const units = quantity * caseQty;
          const totalWeightOz = units * ozFill;
          const oilOz = totalWeightOz * OIL_PERCENTAGE;
          const waxOz = totalWeightOz * (1 - OIL_PERCENTAGE);

          totalOil += oilOz / 16; // Convert to lbs
          totalWax += waxOz / 16; // Convert to lbs
          totalUnits += units;
        }
      });
    });

    setTotals({
      totalOil: totalOil.toFixed(2),
      totalWax: totalWax.toFixed(2),
      totalWeight: (totalOil + totalWax).toFixed(2),
      totalUnits: totalUnits
    });
  };

  const toggleGroup = (fragranceName) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(fragranceName)) {
      newExpanded.delete(fragranceName);
    } else {
      newExpanded.add(fragranceName);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleWorkOrderSelection = (orderId, order) => {
    // Check if work order has any remaining quantity
    const remainingQty = order.quantityRemaining !== undefined ? order.quantityRemaining : order.quantity;

    if (remainingQty === 0) {
      alert(`Work order ${order.workOrderNumber} has no remaining quantity to batch`);
      return;
    }

    const newSelected = new Set(selectedWorkOrders);
    const newQuantities = { ...workOrderQuantities };

    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
      delete newQuantities[orderId];
    } else {
      newSelected.add(orderId);
      // Initialize with remaining quantity
      newQuantities[orderId] = remainingQty;
    }

    setSelectedWorkOrders(newSelected);
    setWorkOrderQuantities(newQuantities);
  };

  const handleQuantityChange = (orderId, value, maxQuantity) => {
    const quantity = parseInt(value) || 0;
    if (quantity >= 0 && quantity <= maxQuantity) {
      setWorkOrderQuantities(prev => ({
        ...prev,
        [orderId]: quantity
      }));
    }
  };

  const selectAllInFragrance = (group) => {
    const newSelected = new Set(selectedWorkOrders);
    const newQuantities = { ...workOrderQuantities };

    // Filter out work orders that have no remaining quantity
    const selectableOrders = group.workOrders.filter(order => {
      const remainingQty = order.quantityRemaining !== undefined ? order.quantityRemaining : order.quantity;
      return remainingQty > 0;
    });

    if (selectableOrders.length === 0) {
      alert('All work orders in this fragrance have no remaining quantity to batch');
      return;
    }

    const allSelected = selectableOrders.every(order => newSelected.has(order.id));

    selectableOrders.forEach(order => {
      const remainingQty = order.quantityRemaining !== undefined ? order.quantityRemaining : order.quantity;
      if (allSelected) {
        newSelected.delete(order.id);
        delete newQuantities[order.id];
      } else {
        newSelected.add(order.id);
        newQuantities[order.id] = remainingQty; // Initialize with remaining quantity
      }
    });

    setSelectedWorkOrders(newSelected);
    setWorkOrderQuantities(newQuantities);
  };

  const handleCreateBatch = async () => {
    if (creatingBatch) return;

    // Group selected work orders by fragrance
    const selectedByFragrance = {};

    fragranceGroups.forEach(group => {
      const selectedInGroup = group.workOrders.filter(order =>
        selectedWorkOrders.has(order.id)
      ).map(order => ({
        ...order,
        batch_quantity: workOrderQuantities[order.id] || order.quantity
      }));

      if (selectedInGroup.length > 0) {
        selectedByFragrance[group.fragranceName] = {
          ...group,
          workOrders: selectedInGroup
        };
      }
    });

    const fragranceNames = Object.keys(selectedByFragrance);

    if (fragranceNames.length === 0) {
      alert('Please select work orders to create a batch');
      return;
    }

    if (fragranceNames.length > 1) {
      alert('Please select work orders from only one fragrance');
      return;
    }

    // Check if any selected orders have zero quantity
    const fragranceData = selectedByFragrance[fragranceNames[0]];
    const hasZeroQuantity = fragranceData.workOrders.some(
      order => (workOrderQuantities[order.id] || order.quantity) === 0
    );

    if (hasZeroQuantity) {
      alert('Please enter a quantity greater than 0 for all selected work orders');
      return;
    }

    // Create batch directly
    setCreatingBatch(true);
    try {
      // Prepare work orders data - include ALL fields
      const workOrdersData = fragranceData.workOrders.map(wo => ({
        ...wo,
        workOrderNumber: wo.workOrderNumber,
        quantity: wo.quantity,
        batch_quantity: wo.batch_quantity || workOrderQuantities[wo.id],
        caseQty: wo.caseQty,
        ozFill: wo.ozFill,
        itemSku: wo.itemSku,
        itemDescription: wo.itemDescription,
        customerName: wo.customerName,
        // Include cart and vessel fields that were missing
        cartQuantity: wo.cartQuantity,
        vesselId: wo.vesselId,
        vesselName: wo.vesselName,
        vesselSku: wo.vesselSku,
        fragranceOilName: wo.fragranceOilName,
        fragranceOilId: wo.fragranceOilId
      }));

      // Create batch with auto-generated name
      const response = await axios.post('/api/batches', {
        batchData: {
          fragrance_oil_id: fragranceData.fragranceId,
          fragrance_oil_name: fragranceData.fragranceName,
          status: 'draft'
        },
        workOrders: workOrdersData
      });

      // Clear selections and refresh
      setSelectedWorkOrders(new Set());
      setWorkOrderQuantities({});
      fetchFragranceGroups();

      // Show success message with batch name
      alert(`Batch "${response.data.batch_name}" created successfully!`);
    } catch (error) {
      console.error('Error creating batch:', error);
      alert('Failed to create batch: ' + (error.response?.data?.error || error.message));
    } finally {
      setCreatingBatch(false);
    }
  };


  const exportToCSV = () => {
    let csv = 'Fragrance,Oil On Hand (lbs),Oil On Order (lbs),Work Order,Item,Quantity,Case Qty,Oz Fill,Total Units,Oil Needed (lbs),Wax Needed (lbs)\n';

    fragranceGroups.forEach(group => {
      const onHand = group.oilOnHand !== undefined ? group.oilOnHand : 'N/A';
      const onOrder = group.oilOnOrder !== undefined ? group.oilOnOrder : '0';
      group.workOrders.forEach(order => {
        csv += `"${group.fragranceName}","${onHand}","${onOrder}","${order.workOrderNumber}","${order.itemDescription}",${order.quantity},${order.caseQty},${order.ozFill},${order.totalUnits},${order.oilNeeded},${order.waxNeeded}\n`;
      });
    });

    // Add summary
    csv += `\nSUMMARY\n`;
    csv += `Total Oil Required,${totals.totalOil} lbs\n`;
    csv += `Total Wax Required,${totals.totalWax} lbs\n`;
    csv += `Total Weight,${totals.totalWeight} lbs\n`;

    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fragrance-materials-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchFragranceGroups} className="retry-btn">Retry</button>
      </div>
    );
  }

  return (
    <div className="fragrance-group-view">
      <div className="sticky-header-container">
        <div className="totals-summary">
          <div className="selection-info">
            <h3>Selected Work Orders</h3>
            <div className="selection-stats">
              {selectedWorkOrders.size} orders selected
            </div>
          </div>
          <div className="total-card units-card">
            <div className="total-label">Total Units</div>
            <div className="total-value">{totals.totalUnits}</div>
          </div>
          <div className="total-card oil-card">
            <div className="total-label">Oil Required</div>
            <div className="total-value">{totals.totalOil} lbs</div>
          </div>
          <div className="total-card wax-card">
            <div className="total-label">Wax Required</div>
            <div className="total-value">{totals.totalWax} lbs</div>
          </div>
          <button
            onClick={handleCreateBatch}
            className="create-batch-main-btn"
            disabled={selectedWorkOrders.size === 0 || creatingBatch}
          >
            {creatingBatch ? 'Creating...' : 'Create Batch'}
          </button>
        </div>

        <div className="table-header">
          <span className="expand-icon"></span>
          <span className="select-all-header"></span>
          <div className="fragrance-name">Fragrance Oil</div>
          <div className="col-units">Units</div>
          <div className="col-oil-needed">Oil (lbs)</div>
          <div className="col-wax-needed">Wax (lbs)</div>
          <div className="col-on-hand">On Hand</div>
          <div className="col-on-order">On Order</div>
          <div className="col-total">Total (lbs)</div>
        </div>
      </div>

      <div className="fragrance-groups">
        {fragranceGroups.length === 0 ? (
          <div className="no-data">No work orders with fragrance data found</div>
        ) : (
          <>
            {fragranceGroups.map((group, index) => (
            <div key={group.fragranceName || index} className="fragrance-group">
              <div
                className="group-header"
                onClick={() => toggleGroup(group.fragranceName)}
              >
                <span className="expand-icon">
                  {expandedGroups.has(group.fragranceName) ? '▼' : '▶'}
                </span>
                <input
                  type="checkbox"
                  className="select-all-checkbox"
                  checked={group.workOrders.every(order => selectedWorkOrders.has(order.id))}
                  onChange={(e) => {
                    e.stopPropagation();
                    selectAllInFragrance(group);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="fragrance-name">
                  {group.fragranceName || 'No Fragrance Specified'}
                  <span className="order-count">
                    ({group.workOrders.length} orders)
                  </span>
                  {group.highestPriority && group.highestPriority !== 'Normal' && (
                    <span className={`priority-badge priority-${group.highestPriority.toLowerCase()}`}
                          style={{
                            marginLeft: '8px',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            backgroundColor: group.highestPriority === 'Critical' ? '#dc143c' :
                                           group.highestPriority === 'High' ? '#ff8c00' : '#666',
                            color: 'white'
                          }}>
                      {group.highestPriority}
                    </span>
                  )}
                </div>
                <div className="col-units">{group.totalUnits}</div>
                <div className="col-oil-needed">{group.totalOilNeeded}</div>
                <div className="col-wax-needed">{group.totalWaxNeeded}</div>
                <div className="col-on-hand">{group.oilOnHand || '-'}</div>
                <div className="col-on-order">{group.oilOnOrder && parseFloat(group.oilOnOrder) > 0 ? group.oilOnOrder : '-'}</div>
                <div className="col-total">{group.totalWeight}</div>
              </div>

              {expandedGroups.has(group.fragranceName) && (
                <div className="group-details">
                  <table className="work-orders-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Priority</th>
                        <th>WO Number</th>
                        <th>Customer</th>
                        <th>Item</th>
                        <th>Original Qty</th>
                        <th>Remaining</th>
                        <th>Batch Qty</th>
                        <th>Case Qty</th>
                        <th>Oz Fill</th>
                        <th>Units</th>
                        <th>Oil (lbs)</th>
                        <th>Wax (lbs)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.workOrders.map(order => {
                        const remainingQty = order.quantityRemaining !== undefined ? order.quantityRemaining : order.quantity;
                        const hasNoRemaining = remainingQty === 0;

                        return (
                        <tr
                          key={order.id}
                          className={`${selectedWorkOrders.has(order.id) ? 'selected-row' : ''} ${hasNoRemaining ? 'fully-batched-row' : order.isPartiallyBatched ? 'partially-batched-row' : ''}`}
                          title={order.isPartiallyBatched ? `${order.quantityAllocated} of ${order.originalQuantity || order.quantity} already batched` :
                                 hasNoRemaining ? 'Fully batched' : ''}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedWorkOrders.has(order.id)}
                              onChange={() => toggleWorkOrderSelection(order.id, order)}
                              disabled={hasNoRemaining}
                            />
                          </td>
                          <td>
                            {order.priority && order.priority !== 'Normal' && (
                              <span className={`priority-badge priority-${order.priority.toLowerCase()}`}
                                    style={{
                                      padding: '2px 6px',
                                      borderRadius: '3px',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      backgroundColor: order.priority === 'Critical' ? '#dc143c' :
                                                     order.priority === 'High' ? '#ff8c00' : '#666',
                                      color: 'white'
                                    }}>
                                {order.priority}
                              </span>
                            )}
                          </td>
                          <td className="wo-number">
                            <a
                              href={`https://3646798.app.netsuite.com/app/accounting/transactions/workord.nl?id=${order.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: order.quantityRemaining === 0 ? '#999' :
                                       order.isPartiallyBatched ? '#ff8c00' : '#4299e1',
                                textDecoration: order.quantityRemaining === 0 ? 'line-through' : 'none'
                              }}
                            >
                              {order.workOrderNumber}
                            </a>
                            {(order.isPartiallyBatched || order.quantityRemaining === 0) && (
                              <span style={{
                                marginLeft: '8px',
                                fontSize: '11px',
                                color: '#666',
                                fontStyle: 'italic'
                              }}>
                                ({order.quantityAllocated || 0} of {order.originalQuantity || order.quantity} batched)
                              </span>
                            )}
                          </td>
                          <td>
                            {order.customerName || 'N/A'}
                          </td>
                          <td>
                            <div className="item-info">
                              <div className="item-sku">{order.itemSku}</div>
                              <div className="item-desc">{order.itemDescription}</div>
                            </div>
                          </td>
                          <td className="number">
                            {order.quantityRemaining !== undefined && order.quantityRemaining !== order.quantity ? (
                              <span title={`${order.quantityAllocated || 0} already batched`}>
                                {order.quantityRemaining}/{order.originalQuantity || order.quantity}
                              </span>
                            ) : (
                              order.quantity
                            )}
                          </td>
                          <td className="number" style={{ color: order.quantityRemaining > 0 ? '#4CAF50' : '#999' }}>
                            {order.quantityRemaining || order.quantity - (order.quantityAllocated || 0) - (order.quantityBuilt || 0)}
                          </td>
                          <td className="number">
                            {selectedWorkOrders.has(order.id) ? (
                              <input
                                type="number"
                                className="quantity-input"
                                value={workOrderQuantities[order.id] || (order.quantityRemaining || order.quantity - (order.quantityAllocated || 0) - (order.quantityBuilt || 0))}
                                onChange={(e) => handleQuantityChange(order.id, e.target.value, order.quantityRemaining || order.quantity - (order.quantityAllocated || 0) - (order.quantityBuilt || 0))}
                                min="1"
                                max={order.quantityRemaining || order.quantity - (order.quantityAllocated || 0) - (order.quantityBuilt || 0)}
                                style={{ width: '60px', textAlign: 'center' }}
                              />
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="number">{order.caseQty || 1}</td>
                          <td className="number">{order.ozFill || 0}</td>
                          <td className="number highlight">
                            {selectedWorkOrders.has(order.id)
                              ? (workOrderQuantities[order.id] || order.quantity) * (order.caseQty || 1)
                              : order.totalUnits}
                          </td>
                          <td className="number oil-value">
                            {selectedWorkOrders.has(order.id)
                              ? (((workOrderQuantities[order.id] || order.quantity) * (order.caseQty || 1) * (order.ozFill || 0) * OIL_PERCENTAGE) / 16).toFixed(2)
                              : order.oilNeeded}
                          </td>
                          <td className="number wax-value">
                            {selectedWorkOrders.has(order.id)
                              ? (((workOrderQuantities[order.id] || order.quantity) * (order.caseQty || 1) * (order.ozFill || 0) * (1 - OIL_PERCENTAGE)) / 16).toFixed(2)
                              : order.waxNeeded}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="group-total-row">
                        <td colSpan="8" className="text-right"><strong>Group Totals:</strong></td>
                        <td className="number"><strong>{group.totalUnits}</strong></td>
                        <td className="number oil-value"><strong>{group.totalOilNeeded}</strong></td>
                        <td className="number wax-value"><strong>{group.totalWaxNeeded}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
            ))}
          </>
        )}
      </div>

    </div>
  );
}

export default FragranceGroupView;